import React, {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Badge, Button, Col, Container, Row, Spinner, Table} from "react-bootstrap";
import * as XLSX from "xlsx";
import DateRangeSelector from "../DateRangeSelector";
import AdhocImageGallary from "../AdhocImageGallary";
import {listJobComplaints, normalizeComplaint} from "../../service/JobComplaintService";
import {toDateString} from "../../service/JobService";

function initialRange() {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {startDate, endDate, key: "selection"};
}

export function complaintExportRows(rows) {
    return rows.map(item => ({
        "Complaint date": new Date(item.complainedAt).toLocaleString(),
        "Job ID": item.cosJobId,
        "Job date": item.jobDate,
        Address: item.fullAddress,
        Worker: item.workerName,
        Reason: item.reason,
        "Wage deduction": Number(item.deductionAmount || 0),
        "Job credit": Number(item.creditPrice || 0),
        "Accounting status": item.accountingStatus,
        "Submitted by": item.complainedByUsername,
    }));
}

export default function JobComplaintsPage() {
    const [range, setRange] = useState(initialRange);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const requestId = useRef(0);

    const load = useCallback(async () => {
        const currentRequest = ++requestId.current;
        setLoading(true);
        setError("");
        try {
            const response = await listJobComplaints(range.startDate, range.endDate);
            if (!response.ok) throw new Error(await response.text() || "Unable to load complaints");
            const data = (await response.json()).map(normalizeComplaint);
            if (currentRequest === requestId.current) setRows(data);
        } catch (err) {
            if (currentRequest === requestId.current) setError(err.message);
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, [range]);

    useEffect(() => { load(); }, [load]);

    function exportRows() {
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(complaintExportRows(rows)), "Complaints");
        XLSX.writeFile(workbook, `job_complaints_${toDateString(range.startDate)}_${toDateString(range.endDate)}.xlsx`);
    }

    return <Container fluid className="pt-3">
        <Row className="align-items-center gy-2 mb-3">
            <Col className="text-start"><h4 className="mb-0">Job Complaints</h4></Col>
            <Col xs="auto"><DateRangeSelector startDate={range.startDate} endDate={range.endDate} onDateChange={event => setRange(event.selection)}/></Col>
            <Col xs="auto"><Button variant="outline-secondary" onClick={load} disabled={loading}>Refresh</Button></Col>
            <Col xs="auto"><Button onClick={exportRows} disabled={!rows.length}>Export Excel</Button></Col>
        </Row>
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {loading ? <div className="text-center"><Spinner animation="border"/></div> : <Table responsive striped hover>
            <thead><tr><th>Complaint date</th><th>Job</th><th>Worker</th><th>Reason</th><th>Wage deduction</th><th>Credit</th><th>Status</th><th>Photos</th></tr></thead>
            <tbody>{rows.map(item => <tr key={item.id}>
                <td>{new Date(item.complainedAt).toLocaleString()}</td>
                <td><b>{item.cosJobId}</b><br/><small>{item.jobDate}<br/>{item.fullAddress}</small></td>
                <td>{item.workerName}</td>
                <td style={{whiteSpace: "pre-wrap", minWidth: 220}}>{item.reason}</td>
                <td>${Number(item.deductionAmount || 0).toFixed(2)}</td>
                <td>${Number(item.creditPrice || 0).toFixed(2)}</td>
                <td><Badge bg={item.accountingStatus === "COMPLETED" ? "success" : item.accountingStatus === "PENDING" ? "warning" : "secondary"}>{item.accountingStatus}</Badge></td>
                <td><AdhocImageGallary images={item.images || []} isReadOnly/></td>
            </tr>)}</tbody>
        </Table>}
        {!loading && !rows.length ? <p className="text-muted text-center">No complaints in this submitted-date range.</p> : null}
    </Container>;
}
